import React, { useState } from 'react';
import util from '../../styles/utilities.module.css';
import { useGetQuizResultsQuery } from '../../slices/credentialingApi';
import './QuizResults.css';

const QuizResults = ({ 
  moduleId, 
  progress, 
  moduleData,
  onStartQuiz,
  className = '' 
}) => {
  const [showQuizResults, setShowQuizResults] = useState(false);
  
  const { 
    data: quizResultsData,
    isLoading: quizResultsLoading,
    error: quizResultsError
  } = useGetQuizResultsQuery(moduleId, {
    skip: !showQuizResults || !moduleId
  });

  if (!moduleData?.has_quiz) {
    return null;
  }

  return (
    <div className={`quiz-results ${className}`}>
      {/* Quiz Status and Results Viewing */}
      {progress?.quiz_attempts > 0 && (
        <div className="training-module__quiz-status">
          <h3>📊 Quiz Status</h3>
          <div className="quiz-status-content">
            <div className="quiz-stats">
              <div className="stat-item">
                <span className="stat-label">Best Score:</span>
                <span className={`stat-value ${progress.quiz_passed ? 'passed' : 'needs-improvement'}`}>
                  {Math.round(progress.quiz_best_score || 0)}%
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Attempts:</span>
                <span className="stat-value">{progress.quiz_attempts}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Status:</span>
                <span className={`stat-value status ${progress.quiz_status}`}>
                  {progress.quiz_status === 'passed' ? '✅ Passed' : 
                   progress.quiz_status === 'failed' ? '❌ Failed' : 
                   '⏳ In Progress'}
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Passing Score:</span>
                <span className="stat-value">{moduleData.quiz_passing_score}%</span>
              </div>
            </div>
            
            <div className="quiz-actions">
              <button 
                className={`${util.btn} ${util['btn--ghost']} quiz-action-btn`}
                onClick={() => setShowQuizResults(true)}
              >
                📋 View Quiz Results
              </button>
              
              {(!progress.quiz_passed || moduleData.allow_retakes) && (
                <button 
                  className={`${util.btn} ${util['btn--primaryGradient']} quiz-action-btn`}
                  onClick={onStartQuiz}
                >
                  📝 View Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quiz Results Modal/View */}
      {showQuizResults && (
        <div className="training-module__quiz-results-modal">
          <div className="quiz-results-content">
            <div className="quiz-results-header">
              <h3>📊 Your Quiz Results</h3>
              <button 
                className={`${util['btn-icon']} ${util['btn-icon-sm']} close-button`}
                onClick={() => setShowQuizResults(false)}
                aria-label="Close quiz results"
              >
                ✕
              </button>
            </div>
            
            {quizResultsLoading && (
              <div className="loading-message">Loading quiz results...</div>
            )}
            
            {quizResultsError && (
              <div className={`${util.alert} ${util['alert--danger']}`}>
                {quizResultsError.data?.message || quizResultsError.data?.error || 'Failed to load quiz results. Please try again.'}
                {console.log('Quiz results error:', quizResultsError)}
              </div>
            )}
            
            {quizResultsData?.data && (
              <div className="quiz-results-display">
                <div className="results-summary">
                  <h4>{quizResultsData.data.module_title}</h4>
                  <div className="summary-stats">
                    <div className="summary-stat">
                      <span className="summary-label">Best Score:</span>
                      <span className={`summary-value ${quizResultsData.data.quiz_best_score >= quizResultsData.data.passing_score ? 'passed' : 'failed'}`}>
                        {Math.round(quizResultsData.data.quiz_best_score)}%
                      </span>
                    </div>
                    <div className="summary-stat">
                      <span className="summary-label">Total Attempts:</span>
                      <span className="summary-value">{quizResultsData.data.quiz_attempts}</span>
                    </div>
                    <div className="summary-stat">
                      <span className="summary-label">Status:</span>
                      <span className={`summary-value status ${quizResultsData.data.quiz_status}`}>
                        {quizResultsData.data.quiz_status === 'passed' ? '✅ Passed' : '❌ Not Passed'}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Detailed Results */}
                <div className="detailed-quiz-results">
                  <h4>Question Review</h4>
                  {quizResultsData.data.detailed_results?.map((result, index) => {
                    const indexToLetter = (i) => {
                      if (typeof i !== 'number' || i < 0) return '';
                      let n = i, letters = '';
                      do { letters = String.fromCharCode(65 + (n % 26)) + letters; n = Math.floor(n / 26) - 1; } while (n >= 0);
                      return letters;
                    };
                    const formatIndices = (arr) => arr.map(v => {
                      const n = typeof v === 'string' ? parseInt(v,10) : v;
                      return isNaN(n) ? v : indexToLetter(n);
                    }).join(', ');
                    const formatAnswerGeneric = (raw, multi=false) => {
                      if (raw === null || raw === undefined || raw === '') return 'No answer recorded';
                      if (Array.isArray(raw)) return formatIndices(raw);
                      if (typeof raw === 'boolean') return raw ? 'True' : 'False';
                      if (typeof raw === 'number') return multi ? indexToLetter(raw) : indexToLetter(raw);
                      if (typeof raw === 'string') {
                        const trimmed = raw.trim();
                        if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('"[') && trimmed.endsWith(']"'))) {
                          try { const parsed = JSON.parse(trimmed.replace(/^"|"$/g, '')); if (Array.isArray(parsed)) return formatIndices(parsed); } catch(_) {}
                        }
                        if (/[;,]/.test(trimmed)) {
                          return trimmed.split(/[;,]/).map(s => s.trim()).filter(Boolean).map(s => /^\d+$/.test(s)? indexToLetter(parseInt(s,10)): s).join(', ');
                        }
                        if (/^\d+$/.test(trimmed)) {
                          if (trimmed.length === 1) return indexToLetter(parseInt(trimmed,10));
                          return trimmed.split('').map(d => indexToLetter(parseInt(d,10))).join(', ');
                        }
                        return trimmed;
                      }
                      return String(raw);
                    };
                    const question = quizResultsData.data.questions ? quizResultsData.data.questions[index] : null;
                    const isMulti = question?.type === 'multiple_choice' && question?.multi_select;
                    const isMC = question?.type === 'multiple_choice';
                    const userAns = isMC ? formatAnswerGeneric(result.user_answer, isMulti) : formatAnswerGeneric(result.user_answer);
                    const correctAns = (result.is_correct === false && result.correct_answer !== undefined) ? (isMC ? formatAnswerGeneric(result.correct_answer, isMulti) : formatAnswerGeneric(result.correct_answer)) : null;
                    return (
                      <div key={index} className={`result-item ${result.is_correct === true ? 'correct' : result.is_correct === false ? 'incorrect' : 'neutral'}`}>
                        <div className="result-header">
                          <span className="question-number">Question {index + 1}</span>
                          {result.is_correct !== null && (
                            <span className={`result-indicator ${result.is_correct ? 'correct' : 'incorrect'}`}>
                              {result.is_correct ? '✓' : '✗'}
                            </span>
                          )}
                        </div>
                        <p className="result-question">{result.question}</p>
                        <div className="result-answers">
                          <div className="your-answer">
                            <strong>Your answer:</strong> {userAns}
                          </div>
                          {correctAns && (
                            <div className="correct-answer">
                              <strong>Correct answer:</strong> {correctAns}
                            </div>
                          )}
                          {result.explanation && (
                            <div className="explanation">
                              <strong>Explanation:</strong> {result.explanation}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizResults;